import React, { useState, useEffect, useCallback } from 'react';
import { FileText, X, ChevronRight, AlertCircle, CheckCircle } from 'lucide-react';
import PartDisplay from './../practice/PartDisplay';

const MockExamFloatingButton = ({ mockExamData, examTypeColor, open: controlledOpen, onClose, onOpen, hideFloatingOnMobile }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const setOpen = (v) => {
    if (!isControlled) setInternalOpen(v);
    else if (v && onOpen) onOpen();
    else if (!v && onClose) onClose();
  };
  const [userAnswers, setUserAnswers] = useState({});
  const [flaggedQuestions, setFlaggedQuestions] = useState([]);
  const [showTooltip, setShowTooltip] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [buttonState, setButtonState] = useState('loading'); // loading, available, unavailable

  // Enhanced initialization effect
  useEffect(() => {
    if (mockExamData) {
      setButtonState('available');
      // Hide tooltip after 5 seconds only if data is available
      const timer = setTimeout(() => {
        setShowTooltip(false);
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      setButtonState('unavailable');
      setShowTooltip(false);
    }
  }, [mockExamData]);

  const handleCheckAnswers = () => {
    setIsChecking(true);
    setShowResults(true);
  };

  const handleTryAgain = () => {
    setUserAnswers({});
    setShowResults(false);
    setIsChecking(false);
  };

  const toggleFlagQuestion = useCallback((questionId) => {
    setFlaggedQuestions(prev =>
      prev.includes(questionId) ? prev.filter(id => id !== questionId) : [...prev, questionId]
    );
  }, []);

  // Don't render anything if no mock data is available
  if (!mockExamData || buttonState === 'unavailable') {
    return null;
  }

  const handleAnswerChange = useCallback((partId, answers) => {
    setUserAnswers(prev => ({
      ...prev,
      [partId]: answers
    }));
  }, []);

  const getResultsData = () => {
    if (!mockExamData || !mockExamData.questions) return { correct: 0, total: 0 };

    let correct = 0;
    let total = mockExamData.questions.length;

    mockExamData.questions.forEach(question => {
      const userAnswer = userAnswers[mockExamData.partId]?.[question.questionId];
      if (userAnswer !== undefined && userAnswer !== null && userAnswer !== '') {
        if (userAnswer === question.correctAnswer ||
            userAnswer === String(question.correctAnswer)) {
          correct++;
        }
      }
    });

    return { correct, total };
  };

  // Enhanced tooltip message based on available data
  const getTooltipMessage = () => {
    if (mockExamData.availableExams > 1) {
      return `Prüfungsbeispiel verfügbar! (${mockExamData.availableExams} Varianten)`;
    }
    return 'Klicken Sie hier für ein Prüfungsbeispiel!';
  };

  return (
    <>
      {/* Floating Button, hidden on mobile when hideFloatingOnMobile (use bottom nav instead) */}
      {!isOpen && buttonState === 'available' && (
        <div className={`fixed top-20 right-6 z-50 ${hideFloatingOnMobile ? 'hidden sm:block' : ''}`}>
          {/* Enhanced Popup Message */}
          {showTooltip && (
            <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 animate-fade-in">
              <div className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm whitespace-nowrap relative animate-bounce-gentle">
                {getTooltipMessage()}

                {/* Arrow pointing to button */}
                <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full">
                  <div className="border-t-8 border-b-8 border-l-8 border-transparent border-l-gray-900"></div>
                </div>

                {/* Small success indicator if multiple variants available */}
                {mockExamData.availableExams > 1 && (
                  <div className="absolute -top-1 -right-1">
                    <div className="w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-2 h-2 text-white" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Enhanced Glowing Button */}
          <button
            onClick={() => setOpen(true)}
            onMouseEnter={() => setShowTooltip(false)}
            className={`relative p-4 rounded-full shadow-2xl text-white bg-gradient-to-r ${examTypeColor} hover:scale-110 transition-all duration-300 group animate-glow`}
            aria-label="Prüfungsbeispiel öffnen"
          >
            {/* Glow rings */}
            <div className="absolute inset-0 rounded-full bg-white opacity-25 animate-ping"></div>
            <div className="absolute inset-0 rounded-full bg-white opacity-20 animate-ping animation-delay-200"></div>

            {/* Icon */}
            <FileText size={24} className="relative z-10" />

            {/* Multiple variants indicator */}
            {mockExamData.availableExams > 1 && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
                {mockExamData.availableExams}
              </div>
            )}

            {/* Enhanced Hover tooltip */}
            <span className="absolute right-full mr-2 px-3 py-1 bg-gray-900 text-white text-sm rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
              Prüfungsbeispiel - {mockExamData.examProvider}
            </span>
          </button>
        </div>
      )}

      {/* Expandable Panel */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden animate-fade-in relative z-50 flex flex-col">
            {/* Enhanced Header */}
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <div>
                <h3 className="font-bold text-lg">Prüfungsbeispiel</h3>
                <p className="text-sm text-gray-600">
                  {mockExamData.sectionName} - {mockExamData.partName}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {mockExamData.examProvider} | Quelle: {mockExamData.examFile || 'Original'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label="Schließen"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)] relative">
              {/* Main content area */}
              <div className={`transition-all duration-300 ${showResults ? 'mr-0 lg:mr-96' : 'mr-0'}`}>
                <PartDisplay
                  partData={mockExamData}
                  savedAnswers={userAnswers[mockExamData.partId] || {}}
                  onAnswerChange={handleAnswerChange}
                  flaggedQuestions={flaggedQuestions}
                  toggleFlagQuestion={toggleFlagQuestion}
                  mode="practice"
                />
              </div>

              {/* Enhanced Slide-out Answer Panel */}
              <div className={`fixed top-0 right-0 h-full bg-white shadow-2xl transform transition-transform duration-300 ease-in-out ${
                showResults ? 'translate-x-0' : 'translate-x-full'
              } w-full lg:w-96 z-40 overflow-y-auto`}>

                {/* Panel Header */}
                <div className="sticky top-0 bg-blue-600 text-white p-4 flex justify-between items-center">
                  <h3 className="font-bold text-lg">Antworten & Ergebnisse</h3>
                  <button
                    onClick={() => setShowResults(false)}
                    className="p-2 rounded-lg hover:bg-blue-700 transition-colors"
                    aria-label="Ergebnisse schließen"
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Enhanced Results Summary */}
                <div className="bg-blue-50 border-b border-blue-200 p-4">
                  <div className="text-center">
                    <h4 className="font-bold text-lg text-blue-900 mb-2">Ihre Ergebnisse</h4>
                    <p className="text-blue-700">
                      <span className="font-bold text-2xl">{getResultsData().correct}</span>
                      <span className="text-sm mx-2">von</span>
                      <span className="font-bold text-2xl">{getResultsData().total}</span>
                      <span className="text-sm ml-2">richtig</span>
                    </p>
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="bg-green-500 h-3 rounded-full transition-all duration-500"
                          style={{ width: `${(getResultsData().correct / getResultsData().total) * 100}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        {Math.round((getResultsData().correct / getResultsData().total) * 100)}% Erfolgsquote
                      </p>
                    </div>
                  </div>
                </div>

                {/* Rest of the answers list remains the same... */}
                <div className="p-4">
                  <div className="space-y-3">
                    {mockExamData.questions?.map((question, idx) => {
                      const userAnswer = userAnswers[mockExamData.partId]?.[question.questionId];
                      const isCorrect = userAnswer === question.correctAnswer ||
                                       userAnswer === String(question.correctAnswer);

                      let correctAnswerDisplay = question.correctAnswer;
                      if (question.options) {
                        const correctOption = question.options.find(opt => opt.id === question.correctAnswer);
                        if (correctOption) {
                          correctAnswerDisplay = `${question.correctAnswer}: ${correctOption.text}`;
                        }
                      }

                      return (
                        <div
                          key={question.questionId}
                          className={`p-4 rounded-lg border-2 ${
                            isCorrect ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                              isCorrect ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                            }`}>
                              {idx + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900 mb-1">
                                Frage {idx + 1}
                              </div>
                              <div className="text-sm text-gray-600 mb-2">
                                <span className="font-medium">Ihre Antwort:</span> {userAnswer || 'Nicht beantwortet'}
                              </div>
                              <div className="text-sm text-gray-600">
                                <span className="font-medium">Richtige Antwort:</span> {correctAnswerDisplay}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t p-4 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {mockExamData.questions?.length || 0} Fragen • {mockExamData.examProvider}
              </div>
              <div className="flex gap-3">
                {!showResults ? (
                  <button
                    onClick={handleCheckAnswers}
                    disabled={Object.keys(userAnswers[mockExamData.partId] || {}).length === 0}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Antworten überprüfen
                  </button>
                ) : (
                  <button
                    onClick={handleTryAgain}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Erneut versuchen
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MockExamFloatingButton;